import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Rating,
  Avatar,
  CardMedia,
  Alert
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Event,
  People,
  Star,
  LightbulbOutlined,
  CalendarToday,
  Person,
  Groups,
  AccountBalanceWallet,
  TableRestaurant,
  TaskAlt,
  MailOutline,
  Celebration,
  AccountCircle,
  Search,
  TrendingUp,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import blogPostService from '../services/blogPostService';
import { useLayout } from '../contexts/LayoutContext';
import { format, parseISO } from 'date-fns';
import hu from 'date-fns/locale/hu';
import HomeDiscoveryBands from '../components/Home/HomeDiscoveryBands';
import SearchAutocomplete from '../components/Search/SearchAutocomplete';
import LoadingScreen from '../components/LoadingScreen/LoadingScreen'; 

// ÚJ: Keyframes a fénylés animációhoz
const shimmerAnimation = keyframes`
  0% { transform: translateX(-100%) skewX(-30deg); }
  5% { transform: translateX(200%) skewX(-30deg); }
  100% { transform: translateX(200%) skewX(-30deg); }
`;

// ÚJ: Styled Button komponens a fényléshez
const ShimmerButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)`,
    transform: 'translateX(-100%) skewX(-30deg)',
    animation: `${shimmerAnimation} 20s ease-in-out infinite`,
  },
}));

// Fade-in animáció a hero főcímhez
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Kategóriák és nagyvárosok a kereső placeholder typewriter effekthez
const SEARCH_CATEGORIES = ['Fotós', 'Videós', 'Catering', 'Dekorátor', 'Tánctanár', 'Virágkötő', 'DJ', 'Zenekar', 'Sminkes', 'Fodrász', 'Tortakészítő', 'Szertartásvezető'];
const SEARCH_CITIES = ['Budapest', 'Debrecen', 'Miskolc', 'Pécs', 'Győr', 'Szeged', 'Nyíregyháza', 'Kecskemét', 'Székesfehérvár', 'Szombathely', 'Eger', 'Veszprém'];

const usePlaceholderTypewriter = () => {
  const [placeholder, setPlaceholder] = useState('');

  useEffect(() => {
    const getRandomCombo = () => {
      const cat = SEARCH_CATEGORIES[Math.floor(Math.random() * SEARCH_CATEGORIES.length)];
      const city = SEARCH_CITIES[Math.floor(Math.random() * SEARCH_CITIES.length)];
      return `${cat} ${city}`;
    };

    let current = '';
    let target = getRandomCombo();
    let isDeleting = false;
    let timeoutId;

    const tick = () => {
      if (!isDeleting) {
        current = target.slice(0, current.length + 1);
        setPlaceholder(current);
        if (current === target) {
          timeoutId = setTimeout(() => { isDeleting = true; tick(); }, 1800);
        } else {
          timeoutId = setTimeout(tick, 80);
        }
      } else {
        current = current.slice(0, -1);
        setPlaceholder(current);
        if (current === '') {
          target = getRandomCombo();
          isDeleting = false;
          timeoutId = setTimeout(tick, 400);
        } else {
          timeoutId = setTimeout(tick, 40);
        }
      }
    };

    timeoutId = setTimeout(tick, 800);
    return () => clearTimeout(timeoutId);
  }, []);

  return placeholder;
};


const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { setHeaderVisible, setIsHeaderTransparent } = useLayout();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const placeholderText = usePlaceholderTypewriter();

  const heroRef = useRef(null);
  const [heroInView, setHeroInView] = useState(true);

  const videoRef = useRef(null);

  const [latestBlogPosts, setLatestBlogPosts] = useState([]);
  const [loadingBlogPosts, setLoadingBlogPosts] = useState(true);


  // === Fejléc láthatóságának kezelése ===
  useEffect(() => {
    setHeaderVisible(true);
    if (setIsHeaderTransparent) {
      setIsHeaderTransparent(heroInView);
    }

    return () => {
      if (setIsHeaderTransparent) {
        setIsHeaderTransparent(false);
      }
      setHeaderVisible(true);
    };
  }, [heroInView, setHeaderVisible, setIsHeaderTransparent]);

  // ÚJ: Blogbejegyzések lekérdezése
  useEffect(() => {
    const fetchLatestBlogPosts = async () => {
      setLoadingBlogPosts(true);
      try {
        const response = await blogPostService.getBlogPosts({ limit: 3, isPublished: true });
        if (response.success) {
          setLatestBlogPosts(response.data);
        }
      } catch (error) {
        console.error('Hiba a blogbejegyzések betöltése során:', error);
      } finally {
        setLoadingBlogPosts(false);
      }
    };
    fetchLatestBlogPosts();
  }, []);



  // Intersection Observer hook
  const useIntersectionObserver = (ref, setInView) => {
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setInView(entry.isIntersecting);
        },
        { threshold: 0.5 }
      );

      if (ref.current) {
        observer.observe(ref.current);
      }

      return () => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      };
    }, [ref, setInView]);
  };

  useIntersectionObserver(heroRef, setHeroInView);

  // Görgetési teljesítmény: a háttérvideó csak akkor fusson, amikor látszik.
  // Képernyőn kívül is folyamatosan dekódoltak / újrarajzoltak, és görgetés közben elvették a kirajzolási
  // kapacitást a többi szekciótól (Chrome/Brave alatt háttérszínű, üres sávok látszottak).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (heroInView) {
      const playPromise = video.play();
      if (playPromise !== undefined) playPromise.catch(() => {}); // autoplay tiltásnál a poster marad
    } else {
      video.pause();
    }
  }, [heroInView]);

  useEffect(() => {
    // Fallback: ha 6 másodpercen belül nem tölt be a videó (pl. Brave blokkolja),
    // a tartalom a poster képpel együtt mégis megjelenik.
    const fallbackTimeout = setTimeout(() => {
      setVideoLoaded(true);
    }, 6000);

    const video = videoRef.current;
    if (!video) {
      return () => clearTimeout(fallbackTimeout);
    }

    const handleCanPlayThrough = () => {
      clearTimeout(fallbackTimeout);
      setVideoLoaded(true);
    };

    const handleLoadedData = () => {
      clearTimeout(fallbackTimeout);
      setTimeout(() => setVideoLoaded(true), 500);
    };

    video.addEventListener('canplaythrough', handleCanPlayThrough);
    video.addEventListener('loadeddata', handleLoadedData);

    if (video.readyState >= 3) {
      clearTimeout(fallbackTimeout);
      setVideoLoaded(true);
    } else {
      // Explicit play() hívás – egyes böngészők (Brave, Safari) csak így indítják el
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay blokkolva (pl. Brave szigorú beállítás) → mutassuk a poster-t
          clearTimeout(fallbackTimeout);
          setVideoLoaded(true);
        });
      }
    }

    return () => {
      clearTimeout(fallbackTimeout);
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, []);


  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/szolgaltatok?search=${encodeURIComponent(searchTerm.trim())}`);
    } else {
      navigate('/szolgaltatok');
    }
  };

  // Javaslatra kattintva: keresés a szolgáltató nevére + előnézet megnyitása a listaoldalon
  const handleSuggestionSelect = (provider) => {
    const name = provider.businessName || searchTerm.trim();
    navigate(`/szolgaltatok?search=${encodeURIComponent(name)}&quickView=${encodeURIComponent(provider.id)}`);
  };

  return (
    <>
      <Helmet>
        <title>Wedigo | Az esküvőd itt kezdődik</title>
        <meta name="description" content="Wedigo – Magyarország esküvői szolgáltatói egy helyen. Találd meg a legjobb fotóst, dekoratőrt, zenészt és más esküvői szakembert." />
        <meta property="og:title" content="Wedigo | Az esküvőd itt kezdődik" />
        <meta property="og:description" content="Wedigo – Magyarország esküvői szolgáltatói egy helyen. Találd meg a legjobb fotóst, dekoratőrt, zenészt és más esküvői szakembert." />
        <meta property="og:url" content="https://wedigo.hu/" />
        <link rel="canonical" href="https://wedigo.hu/" />
      </Helmet>
      {/* Loading Screen */}
      {!videoLoaded && <LoadingScreen isLoaded={videoLoaded} />}

      <Container disableGutters maxWidth={false}>
        {/* Hero szekció */}
        <Box
          ref={heroRef}
          sx={{
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            px: 2,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            opacity: videoLoaded ? 1 : 0,
            transition: 'opacity 0.5s ease-in',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4))',
              zIndex: 0,
            }
          }}
        >
          {/* Háttér videó */}
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="/images/combined_background_poster.jpg"
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: -1,
              filter: 'brightness(0.7)',
            }}
          >
            <source src="/images/combined_background.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>


          {/* Hero tartalom */}
          {videoLoaded && (
            <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* <Typography
                variant="overline"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  letterSpacing: '0.2em',
                  color: 'rgba(255,255,255,0.85)',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
                  display: 'block',
                  mb: 1,
                }}
              >
                ESKÜVŐI PLATFORMOD
              </Typography>   */}
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: { xs: '2.75rem', sm: '4rem', md: '4.75rem' },
                  fontWeight: 400,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                  animation: `${fadeIn} 0.8s ease-out both`,
                }}
              >
                Az esküvőd itt kezdődik.
              </Typography>

              {/* Keresőmező */}
              <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', width: '100%' }}>
                <SearchAutocomplete
                  value={searchTerm}
                  onChange={setSearchTerm}
                  onSearch={handleSearch}
                  onSuggestionSelect={handleSuggestionSelect}
                  placeholder={placeholderText}
                  variant="outlined"
                  endAdornment={
                    <ShimmerButton
                      variant="contained"
                      size="large"
                      onClick={handleSearch}
                      sx={{
                        height: '56px',
                        borderRadius: '0 8px 8px 0',
                        ml: '-1px',
                        backgroundColor: (theme) => theme.palette.primary.dark,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.primary.main,
                        },
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Mutasd a<br />szolgáltatókat
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Mutasd a szolgáltatókat
                      </Box>
                    </ShimmerButton>
                  }
                  sx={{
                    maxWidth: 600,
                    bgcolor: 'rgba(255, 255, 255, 0.34)',
                    borderRadius: '8px',
                    transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out, background-color 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                      transform: 'scale(1.01)',
                    },
                    '&:focus-within': {
                      bgcolor: 'rgba(255, 255, 255, 0.85)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      paddingRight: 0,
                      '&.Mui-focused': {
                        bgcolor: 'transparent',
                      },
                    },
                    '& .MuiInputAdornment-root': {
                      color: 'grey.700',
                    }
                  }}
                />
              </Box>

              {/* Social proof számok – induláskor kikapcsolva, adatok még nem valósak
              <Box sx={{ display: 'flex', gap: { xs: 3, sm: 5 }, justifyContent: 'center', mt: 4, flexWrap: 'wrap' }}>
                {[
                  { number: '2 500+', label: 'szolgáltató' },
                  { number: '18 000+', label: 'pár' },
                  { number: '4.9★', label: 'átlagos értékelés' },
                ].map((item) => (
                  <Box key={item.label} sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
                      {item.number}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', letterSpacing: '0.05em' }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              */}
            </Box>
          )}
        </Box>

      <HomeDiscoveryBands />

      {/* Esküvő Tervező Marketing Szekció */}
      <Box sx={{ py: 10, width: '100%', background: 'linear-gradient(180deg, #f4f8f4 0%, #ffffff 100%)' }}>
        <Container maxWidth="lg">

          {/* Badge */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box component="span" sx={{
              bgcolor: 'primary.light',
              color: 'primary.dark',
              px: 2.5, py: 0.75,
              borderRadius: '20px',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
            }}>
              <Celebration sx={{ fontSize: '0.9rem' }} />
              Ingyenes beépített tervező eszköz
            </Box>
          </Box>

          {/* Főcím */}
          <Typography
            variant="h3"
            align="center"
            sx={{ fontWeight: 700, letterSpacing: '-1px', mb: 1, fontSize: { xs: '1.9rem', sm: '2.5rem', md: '3rem' } }}
          >
            Az esküvőd minden részlete
          </Typography>
          <Typography
            variant="h3"
            align="center"
            sx={{ fontWeight: 700, letterSpacing: '-1px', mb: 2, color: 'primary.main', fontSize: { xs: '1.9rem', sm: '2.5rem', md: '3rem' } }}
          >
            egy helyen
          </Typography>
          <Box sx={{ width: 60, height: 3, bgcolor: 'primary.main', mx: 'auto', mb: 3 }} />
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ fontWeight: 300, mb: 8, maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}
          >
            A Wedigo beépített Esküvő Tervező eszközével semmit sem kell fejben tartani.
            Tervezz, szervezz, és élvezd a nagy napot!
          </Typography>

          {/* Feature kártyák rácsa */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 3,
            mb: 8,
          }}>
            {[
              {
                icon: Groups,
                label: 'Vendéglista',
                desc: 'Kezeld meghívottaidat, kövesd a visszajelzéseket és az ételallergiákat egyetlen felületen.',
                color: '#e8f5e9',
              },
              {
                icon: AccountBalanceWallet,
                label: 'Költségvetés tervező',
                desc: 'Tervezd meg büdzsédet, add meg az egyes tételeket, és kövesd a kiadásokat valós időben.',
                color: '#fff8e1',
              },
              {
                icon: TableRestaurant,
                label: 'Ültetési rend',
                desc: 'Húzd és ejtsd az asztalokat, helyezd el vendégeidet vizuálisan – percek alatt kész az elrendezés.',
                color: '#fce4ec',
              },
              {
                icon: TaskAlt,
                label: 'Feladatlista',
                desc: 'Soha ne maradj le egy teendőről sem. Rendeld hozzá a feladatokat és kövesd az előrehaladást.',
                color: '#e3f2fd',
              },
              {
                icon: MailOutline,
                label: 'Digitális meghívók',
                desc: 'Töltsd fel a meghívódat, küldd ki vendégeidnek egy kattintással, és kövesd, ki erősítette meg a részvételét.',
                color: '#f3e5f5',
              },
              {
                icon: CalendarToday,
                label: 'Eseménynaptár',
                desc: 'Tartsd számon a kóstolókat, ruhapróbákat, határidőket – minden egy naptárban.',
                color: '#e0f7fa',
              },
            ].map((feature) => (
              <Box
                key={feature.label}
                sx={{
                  p: 3,
                  borderRadius: '16px',
                  bgcolor: 'white',
                  boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                  border: '1px solid rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 28px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box sx={{
                  width: 52, height: 52,
                  borderRadius: '14px',
                  bgcolor: feature.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mb: 2,
                }}>
                  <feature.icon sx={{ fontSize: 28, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75, fontSize: '1rem' }}>
                  {feature.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                  {feature.desc}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* CTA */}
          <Box sx={{ textAlign: 'center' }}>
            <ShimmerButton
              variant="contained"
              size="large"
              onClick={() => navigate(isAuthenticated ? '/wedding-planner' : '/register')}
              sx={{
                px: 5, py: 1.75,
                fontSize: '1.05rem',
                fontWeight: 600,
                borderRadius: '10px',
              }}
            >
              Próbáld ki ingyen →
            </ShimmerButton>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Regisztráció szükséges · 100% ingyenes
            </Typography>
          </Box>

        </Container>
      </Box>



      {/* Ügyfeleink mondták szekció – induláskor kikapcsolva, vélemények még nem valósak
      <Box sx={{ py: 10, width: '100%', bgcolor: 'primary.light' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom color="primary.dark" sx={{ fontWeight: 700, letterSpacing: '-1px', fontSize: { xs: '1.9rem', sm: '2.5rem', md: '3rem' } }}>
            A mi történeteink – a te inspirációd
          </Typography>
          <Box sx={{ width: 60, height: 3, bgcolor: 'primary.main', mx: 'auto', mb: 3 }} />
          <Typography variant="h6" align="center" color="primary.dark" sx={{ fontWeight: 300, mb: 8, maxWidth: 560, mx: 'auto', lineHeight: 1.7, opacity: 0.75 }}>
            Valódi párok, valódi történetek – ismerd meg, hogyan segítettük az álomesküvőjük megvalósítását.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 4, justifyContent: 'center' }}>
            <Card
              sx={{
                p: 3,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                bgcolor: 'background.paper',
                flex: '1 1 calc(50% - 16px)',
                maxWidth: { xs: '100%', sm: 'calc(50% - 16px)' },
                display: 'flex',
                flexDirection: 'column',
                borderTop: '4px solid',
                borderColor: 'primary.main',
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', flexGrow: 1 }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 60, height: 60, mb: 2, fontSize: '1.2rem', fontWeight: 700 }}>
                  KA
                </Avatar>
                <Rating value={5} readOnly sx={{ mb: 1 }} />
                <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2 }}>
                  "Hihetetlenül egyszerű volt megtalálni a tökéletes fotóst az esküvőnkre! A Wedigo megmentette a napunkat."
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  Kiss Anna & Péter
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Esküvő, Budapest
                </Typography>
              </CardContent>
            </Card>
            <Card
              sx={{
                p: 3,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                bgcolor: 'background.paper',
                flex: '1 1 calc(50% - 16px)',
                maxWidth: { xs: '100%', sm: 'calc(50% - 16px)' },
                display: 'flex',
                flexDirection: 'column',
                borderTop: '4px solid',
                borderColor: 'primary.main',
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', flexGrow: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60, mb: 2, fontSize: '1.2rem', fontWeight: 700 }}>
                  NG
                </Avatar>
                <Rating value={4.5} readOnly precision={0.5} sx={{ mb: 1 }} />
                <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2 }}>
                  "A platformon keresztül találtam meg a legjobb catering céget a céges rendezvényünkre. Gyors és hatékony volt a folyamat."
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  Nagy Gábor
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Céges rendezvény, Debrecen
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
      */}


      {/* Inspiráció és tippek szekció (Blog előzetes) */}
      <Box sx={{ py: 10, width: '100%', background: 'linear-gradient(180deg, #f4f8f4 0%, #ffffff 100%)' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, letterSpacing: '-1px', fontSize: { xs: '1.9rem', sm: '2.5rem', md: '3rem' } }}>
            Rendezvénytervezési tippek és trendek
          </Typography>
          <Box sx={{ width: 60, height: 3, bgcolor: 'primary.main', mx: 'auto', mb: 3 }} />
          <Typography variant="h6" align="center" color="text.secondary" sx={{ fontWeight: 300, mb: 8, maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
            Hasznos tippek, szezonális trendek és inspiráció a tökéletes rendezvény megvalósításához.
          </Typography>
          {loadingBlogPosts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : latestBlogPosts.length === 0 ? (
            <Alert severity="info" sx={{ mt: 4 }}>
              Jelenleg nincsenek publikált blogbejegyzések.
            </Alert>
          ) : (
            <Box
              sx={{
                display: 'flex',
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                py: 2,
                gap: 3,
                px: { xs: 1, sm: 2, md: 3 },
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              {latestBlogPosts.map((post) => (
                <Box key={post.id} sx={{ flexShrink: 0, width: { xs: '90%', sm: '45%', md: '30%' }, scrollSnapAlign: 'start' }}>
                  <Card sx={{ height: '100%', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', bgcolor: '#ffffff', display: 'flex', flexDirection: 'column' }}>
                    {post.imageUrl ? (
                      <CardMedia
                        component="img"
                        height="220"
                        image={post.imageUrl}
                        alt={post.title}
                        sx={{ objectFit: 'cover', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}
                      />
                    ) : (
                      <Box sx={{ height: 180, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                        <LightbulbOutlined sx={{ fontSize: 80, color: 'grey.500' }} />
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" gutterBottom sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: '4.5em',
                        textAlign: 'center',
                        alignContent: 'center',
                      }}>
                        {post.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {post.metaDescription || post.content.substring(0, 100) + '...'}
                      </Typography>
                      <Button
                        component={Link}
                        to={`/blog/${post.slug}`}
                        variant="outlined"
                        color="primary"
                        sx={{ mt: 'auto' }}
                        onClick={() => window.scrollTo(0, 0)}
                      >
                        Tovább olvasom
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="outlined" component={Link} to="/blog" onClick={() => window.scrollTo(0, 0)}>
              Összes blogbejegyzés megtekintése
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Szolgáltatóknak szekció */}
      <Box sx={{ py: 10, width: '100%', background: 'linear-gradient(180deg, #e8f5e9 0%, #f4f8f4 100%)' }}>
        <Container maxWidth="lg">
          {/* Badge */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box component="span" sx={{
              bgcolor: 'primary.light',
              color: 'primary.dark',
              px: 2.5, py: 0.75,
              borderRadius: '20px',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
            }}>
              <Star sx={{ fontSize: '0.9rem' }} />
              Szolgáltatóknak
            </Box>
          </Box>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 6, md: 8 },
            alignItems: 'center',
          }}>
            {/* Bal oldal – tartalom */}
            <Box>
              <Typography
                variant="h3"
                sx={{
                  color: 'primary.dark',
                  fontWeight: 700,
                  letterSpacing: '-1px',
                  mb: 2,
                  fontSize: { xs: '1.9rem', sm: '2.5rem', md: '2.8rem' },
                }}
              >
                Légy ott, ahol a leendő párok keresnek
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 300, mb: 5, lineHeight: 1.7 }}>
                Csatlakozz a Wedigo közösségéhez és juttasd el szolgáltatásaidat az esküvőre készülő párokhoz – teljesen ingyenesen.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {[
                  {
                    icon: AccountCircle,
                    title: 'Ingyenes profil',
                    desc: 'Hozz létre profillapot, töltsd fel képeidet és mutasd be szolgáltatásaidat.',
                  },
                  {
                    icon: Search,
                    title: 'Célzott láthatóság',
                    desc: 'Az esküvőt tervező párok pont a te kategóriádban keresnek.',
                  },
                  {
                    icon: MailOutline,
                    title: 'Közvetlen megkeresések',
                    desc: 'Ügyfelek közvetlenül írhatnak rád a platformon keresztül.',
                  },
                  {
                    icon: TrendingUp,
                    title: 'Vélemények és rangsorolás',
                    desc: 'Gyűjts értékeléseket és növeld ismertségedet a piacon.',
                  },
                ].map((item) => (
                  <Box key={item.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{
                      width: 44, height: 44, flexShrink: 0,
                      borderRadius: '12px',
                      bgcolor: 'primary.light',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <item.icon sx={{ color: 'secondary.main', fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.3 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Jobb oldal – CTA */}
            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{
                p: { xs: 4, sm: 5 },
                borderRadius: '20px',
                bgcolor: 'white',
                boxShadow: '0 4px 32px rgba(0,0,0,0.09)',
                border: '1px solid rgba(0,0,0,0.06)',
                width: '100%',
                maxWidth: 380,
              }}>
                <Typography variant="h5" sx={{ color: 'primary.dark', fontWeight: 700, mb: 1.5 }}>
                  Készen állsz?
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.65 }}>
                  Percek alatt elkészítheted profilodat, és máris elérhetővé válsz ezrek számára.
                </Typography>
                <ShimmerButton
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/register?userType=PROVIDER"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: '10px',
                    width: '100%',
                  }}
                >
                  Regisztrálj szolgáltatóként →
                </ShimmerButton>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                  Ingyenes · Kötelezettség nélkül
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA szekció */}
      <Box
        sx={{
          py: 10,
          width: '100%',
          background: 'linear-gradient(135deg, #1a3320 0%, #2d5a27 100%)',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 600, mb: 2, letterSpacing: '-0.5px' }}>
            Kezdd el tervezni álomrendezvényedet még ma!
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.75)', mb: 4, fontWeight: 300 }}>
            Ellenőrzött esküvői szakemberek egy helyen – regisztrálj ingyenesen még ma!
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <ShimmerButton
              variant="contained"
              size="large"
              component={Link}
              to="/register"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '8px',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              Ingyenes regisztráció
            </ShimmerButton>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/szolgaltatok"
              sx={{
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                borderRadius: '8px',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Szolgáltatók böngészése
            </Button>
          </Box>
        </Container>
      </Box>

    </Container>
    </>
  );
};

export default Home;